import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MrpService } from '../../services/mrp.service';
import { LucideAngularModule, Plus, Check, RefreshCw, Layers, AlertCircle, AlertTriangle, Clock, Filter, ArrowUp, ArrowDown, Loader, X, Play, Copy, FilterX, FileText, ChevronDown, ChevronUp, BarChart3, ShoppingCart, Calendar } from 'lucide-angular';
import { ExecutionLog } from '../../models/execution-log.model';
import { SortConfig } from '../../models/sort-config.model';
import { ExecutionDetails } from '../../models/protheus-details.model';
import { ProductsSummary } from '../../models/products-summary.model';
import { PurchaseRequest } from '../../models/purchase-request.model';
import { forkJoin } from 'rxjs';

import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-monitor',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
    templateUrl: './monitor.component.html',
    styleUrls: ['./monitor.component.css'],
    host: { 'class': 'block h-full' }
})
export class MonitorComponent {
    mrpService = inject(MrpService);
    authService = inject(AuthService);
    fb = new FormBuilder();

    readonly icons = { Plus, Check, RefreshCw, Layers, AlertCircle, AlertTriangle, Clock, Filter, ArrowUp, ArrowDown, Loader, X, Play, Copy, FilterX, FileText, ChevronDown, ChevronUp, BarChart3, ShoppingCart, Calendar };

    // Expose signals from service
    executionLogs = this.mrpService.executionLogs;
    kpis = this.mrpService.kpis;
    profiles = this.mrpService.profiles;
    branches = this.mrpService.branches;
    pageSize = this.mrpService.pageSize;
    readonly pageSizeOptions = [50, 100, 500];
    totalItems = this.mrpService.totalItems;
    totalPages = this.mrpService.totalPages;
    currentPage = this.mrpService.currentPage;

    constructor() {
        this.mrpService.startAutoRefresh();

        this.branchSearchCtrl.valueChanges.subscribe(val => this.branchSearchTerm.set(val || ''));
        this.profileSearchCtrl.valueChanges.subscribe(val => this.profileSearchTerm.set(val || ''));
    }

    ngOnDestroy() {
        this.mrpService.stopAutoRefresh();
    }

    refreshData() {
        this.mrpService.refreshMonitorDataWithLoading();
    }

    // --- FILTROS E ORDENAÇÃO ---
    updatedAt = this.mrpService.lastRefreshedAt;
    sortConfig = signal<SortConfig<ExecutionLog>>({ column: 'startDate', direction: 'desc' });
    columnFilters = signal<Record<string, string>>({});
    activeFilterColumn = signal<keyof ExecutionLog | null>(null);

    // --- FILTROS AVANÇADOS (checkbox lists + date range) ---
    filterBranchIds = signal<Set<string>>(new Set());
    filterProfileIds = signal<Set<string>>(new Set());
    filterStatusValues = signal<Set<string>>(new Set());
    filterDateStart = signal('');
    filterDateEnd = signal('');
    filterStartedAtStart = signal('');
    filterStartedAtEnd = signal('');
    filterFinishedAtStart = signal('');
    filterFinishedAtEnd = signal('');
    filterBranchSearch = signal('');
    filterProfileSearch = signal('');

    readonly statusOptions = [
        { label: 'RESERVADO', value: 'Reserved' },
        { label: 'PENDENTE', value: 'Pending' },
        { label: 'ENVIANDO', value: 'Sending' },
        { label: 'CALCULANDO', value: 'Processing' },
        { label: 'CALC. FINALIZADO', value: 'Completed' },
        { label: 'DOC. GERADOS', value: 'DocumentsGenerated' },
        { label: 'FALHA DOCS.', value: 'DocumentFailed' },
        { label: 'CANCELANDO', value: 'Cancelling' },
        { label: 'CANCELADO', value: 'Cancelled' },
        { label: 'ERRO', value: 'Error' },
        { label: 'EXCLUÍDO', value: 'Deleted' }
    ];

    readonly filterableBranches = computed(() => {
        const term = this.filterBranchSearch().toLowerCase();
        if (!term) return this.branches();
        return this.branches().filter(b =>
            b.code.toLowerCase().includes(term) || b.name.toLowerCase().includes(term)
        );
    });

    readonly filterableProfiles = computed(() => {
        const term = this.filterProfileSearch().toLowerCase();
        if (!term) return this.profiles();
        return this.profiles().filter(p => p.name.toLowerCase().includes(term));
    });

    // Indica se há algum filtro ativo (para botão "Limpar Filtros")
    readonly hasActiveFilters = computed(() =>
        this.filterBranchIds().size > 0 ||
        this.filterProfileIds().size > 0 ||
        this.filterStatusValues().size > 0 ||
        this.filterDateStart() !== '' ||
        this.filterDateEnd() !== '' ||
        this.filterStartedAtStart() !== '' ||
        this.filterStartedAtEnd() !== '' ||
        this.filterFinishedAtStart() !== '' ||
        this.filterFinishedAtEnd() !== ''
    );

    toggleFilterItem(set: Set<string>, id: string): Set<string> {
        const newSet = new Set(set);
        if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
        return newSet;
    }

    toggleBranchFilter(id: string) {
        this.filterBranchIds.update(s => this.toggleFilterItem(s, id));
    }

    toggleProfileFilter(id: string) {
        this.filterProfileIds.update(s => this.toggleFilterItem(s, id));
    }

    toggleStatusFilter(value: string) {
        this.filterStatusValues.update(s => this.toggleFilterItem(s, value));
    }

    selectAllBranches() {
        this.filterBranchIds.set(new Set(this.filterableBranches().map(b => b.id)));
    }

    clearBranchSelection() {
        this.filterBranchIds.set(new Set());
    }

    selectAllProfiles() {
        this.filterProfileIds.set(new Set(this.filterableProfiles().map(p => p.id)));
    }

    clearProfileSelection() {
        this.filterProfileIds.set(new Set());
    }

    selectAllStatuses() {
        this.filterStatusValues.set(new Set(this.statusOptions.map(s => s.value)));
    }

    clearStatusSelection() {
        this.filterStatusValues.set(new Set());
    }

    clearAllFilters() {
        this.columnFilters.set({});
        this.filterBranchIds.set(new Set());
        this.filterProfileIds.set(new Set());
        this.filterStatusValues.set(new Set());
        this.filterDateStart.set('');
        this.filterDateEnd.set('');
        this.filterStartedAtStart.set('');
        this.filterStartedAtEnd.set('');
        this.filterFinishedAtStart.set('');
        this.filterFinishedAtEnd.set('');
        this.filterBranchSearch.set('');
        this.filterProfileSearch.set('');
        this.activeFilterColumn.set(null);
        this.mrpService.clearAllFilters();
    }

    applyCurrentFilters() {
        this.activeFilterColumn.set(null);
        const sc = this.sortConfig();
        this.mrpService.applyFiltersAndSort(
            this.columnFilters(),
            sc.column as string,
            sc.direction,
            {
                branchUuids: [...this.filterBranchIds()],
                profileUuids: [...this.filterProfileIds()],
                statuses: [...this.filterStatusValues()],
                dateStart: this.filterDateStart(),
                dateEnd: this.filterDateEnd(),
                startedAtStart: this.filterStartedAtStart(),
                startedAtEnd: this.filterStartedAtEnd(),
                finishedAtStart: this.filterFinishedAtStart(),
                finishedAtEnd: this.filterFinishedAtEnd()
            }
        );
    }

    // Dados já vêm filtrados/ordenados do backend
    processedLogs = this.executionLogs;

    // --- MODALS STATE ---
    forceExecutionId = signal<string | null>(null);
    actionErrorMessage = signal<string | null>(null);
    isNewExecutionModalOpen = signal<boolean>(false);
    newExecutionForm: FormGroup = this.fb.group({});

    selectedProfileIds = signal<string[]>([]);
    selectedBranchIds = signal<string[]>([]);
    branchSearchCtrl = new FormControl('');
    profileSearchCtrl = new FormControl('');

    // Aspecto de pesquisa selecionado ('' = todos)
    readonly branchSearchAspect = signal<'' | 'code' | 'name' | 'state'>('');
    readonly profileSearchAspect = signal<'' | 'name' | 'code'>('');

    readonly branchAspectOptions = [
        { value: '', label: 'Todos' },
        { value: 'code', label: 'Código' },
        { value: 'name', label: 'Nome' },
        { value: 'state', label: 'UF' },
    ] as const;

    readonly profileAspectOptions = [
        { value: '', label: 'Todos' },
        { value: 'code', label: 'Código' },
        { value: 'name', label: 'Nome' },
    ] as const;

    filteredBranches = computed(() => {
        const term = this.branchSearchTerm().toLowerCase();
        const aspect = this.branchSearchAspect();
        const filtered = this.branches().filter(b => {
            if (!term) return true;
            switch (aspect) {
                case 'code': return b.code.toLowerCase().includes(term);
                case 'name': return b.name.toLowerCase().includes(term);
                case 'state': return b.state?.toLowerCase().includes(term) ?? false;
                default: return (
                    b.code.toLowerCase().includes(term) ||
                    b.name.toLowerCase().includes(term) ||
                    (b.state && b.state.toLowerCase().includes(term))
                );
            }
        });
        // Filiais com calculateMrp=true primeiro
        return [...filtered].sort((a, b) => (a.calculateMrp === b.calculateMrp ? 0 : a.calculateMrp ? -1 : 1));
    });

    filteredProfiles = computed(() => {
        const term = this.profileSearchTerm().toLowerCase();
        const aspect = this.profileSearchAspect();
        return this.profiles().filter(p => {
            if (!term) return true;
            switch (aspect) {
                case 'code': return p.TELA_1.HW2_CODIGO.toLowerCase().includes(term);
                case 'name': return p.name.toLowerCase().includes(term);
                default: return (
                    p.name.toLowerCase().includes(term) ||
                    p.TELA_1.HW2_CODIGO.toLowerCase().includes(term)
                );
            }
        });
    });

    branchSearchTerm = signal('');
    profileSearchTerm = signal('');

    totalExecutionsCount = computed(() => {
        return this.selectedProfileIds().length * this.selectedBranchIds().length;
    });
    // --- SORTING & FILTERING ACTIONS ---

    toggleSort(column: keyof ExecutionLog) {
        const sc = this.sortConfig();
        if (sc.column === column) {
            this.sortConfig.set({ column, direction: sc.direction === 'asc' ? 'desc' : (sc.direction === 'desc' ? null : 'asc') });
        } else {
            this.sortConfig.set({ column, direction: 'asc' });
        }
        this.applyCurrentFilters();
    }

    toggleFilterPopup(column: keyof ExecutionLog, event: Event) {
        event.stopPropagation();
        if (this.activeFilterColumn() === column) {
            this.activeFilterColumn.set(null);
        } else {
            this.activeFilterColumn.set(column);
        }
    }

    closeFilterPopup() {
        this.applyCurrentFilters();
    }

    clearFilter(column: keyof ExecutionLog) {
        this.columnFilters.update(filters => {
            const newFilters = { ...filters };
            delete newFilters[column];
            return newFilters;
        });
        // Clear structured filter for this column
        if (column === 'branchCode' || column === 'branchName') {
            this.filterBranchIds.set(new Set());
            this.filterBranchSearch.set('');
        } else if (column === 'profileName') {
            this.filterProfileIds.set(new Set());
            this.filterProfileSearch.set('');
        } else if (column === 'status') {
            this.filterStatusValues.set(new Set());
        } else if (column === 'startDate') {
            this.filterDateStart.set('');
            this.filterDateEnd.set('');
        } else if (column === 'startedAt') {
            this.filterStartedAtStart.set('');
            this.filterStartedAtEnd.set('');
        } else if (column === 'finishedAt') {
            this.filterFinishedAtStart.set('');
            this.filterFinishedAtEnd.set('');
        }
        this.applyCurrentFilters();
    }

    updateFilter(column: keyof ExecutionLog, event: Event) {
        const target = event.target as HTMLInputElement;
        this.columnFilters.update(filters => ({ ...filters, [column]: target.value }));
    }

    copyToClipboard(text: string, event: Event) {
        event.stopPropagation();
        navigator.clipboard.writeText(text).then(() => {
            console.log('Ticket copiado:', text);
            // Optional: Show a temporary tooltip or toast
        }).catch(err => {
            console.error('Falha ao copiar:', err);
        });
    }

    formatTimeElapsed(time: string | undefined): string {
        if (!time || time === '-') return '-';
        // Formato esperado: "HH:MM:SS.microseconds" → retorna "HH:MM:SS"
        const parts = time.split(':');
        if (parts.length >= 3) {
            const hh = parts[0].padStart(2, '0');
            const mm = parts[1].padStart(2, '0');
            const ss = parts[2].split('.')[0].padStart(2, '0');
            return `${hh}:${mm}:${ss}`;
        }
        return time;
    }

    formatIsoDate(isoString: string | undefined): string {
        if (!isoString) return '-';
        const d = new Date(isoString);
        if (isNaN(d.getTime())) return '-';
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${pad(d.getFullYear() % 100)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }


    // --- EXECUTION ACTIONS ---
    cancelExecutionId = signal<string | null>(null);

    confirmCancelExecution(id: string) {
        if (!this.authService.hasRole(['Admin', 'Operator'])) return;
        this.cancelExecutionId.set(id);
    }

    cancelCancelExecution() {
        this.cancelExecutionId.set(null);
    }

    executeCancel() {
        const idToCancel = this.cancelExecutionId();
        if (idToCancel) {
            this.mrpService.cancelExecution(idToCancel);
        }
        this.cancelExecutionId.set(null);
    }

    confirmForceExecution(id: string) {
        if (!this.authService.hasRole(['Admin', 'Operator'])) return;
        this.forceExecutionId.set(id);
    }

    cancelForceExecution() {
        this.forceExecutionId.set(null);
    }

    executeForce() {
        const idToForce = this.forceExecutionId();
        if (idToForce) {
            this.forceExecutionId.set(null);
            this.mrpService.retryExecution(idToForce).subscribe({
                next: () => {
                    this.mrpService.refreshMonitorData();
                },
                error: (err: any) => {
                    const msg = err?.error?.message || err?.error?.title || err?.message || 'Erro desconhecido ao forçar execução.';
                    this.actionErrorMessage.set(msg);
                }
            });
        } else {
            this.forceExecutionId.set(null);
        }
    }

    openNewExecutionModal() {
        if (!this.authService.hasRole(['Admin', 'Operator'])) return;
        this.isNewExecutionModalOpen.set(true);
        this.selectedProfileIds.set([]);
        this.selectedBranchIds.set([]);
        this.branchSearchCtrl.setValue('');
        this.profileSearchCtrl.setValue('');
        this.branchSearchAspect.set('');
        this.profileSearchAspect.set('');
        this.newExecutionForm.reset();
    }

    closeNewExecutionModal() {
        this.isNewExecutionModalOpen.set(false);
    }

    toggleSelection(type: 'profiles' | 'branches', id: string) {
        if (type === 'branches') {
            const branch = this.branches().find(b => b.id === id);
            if (branch && !branch.calculateMrp) return;
            const current = this.selectedBranchIds();
            if (current.includes(id)) {
                this.selectedBranchIds.set(current.filter(x => x !== id));
            } else {
                this.selectedBranchIds.set([...current, id]);
            }
        } else {
            // Perfil: seleção exclusiva (apenas um por vez)
            const current = this.selectedProfileIds();
            if (current.includes(id)) {
                this.selectedProfileIds.set([]);
            } else {
                this.selectedProfileIds.set([id]);
            }
        }
    }

    isBranchSelectedForExecution(id: string) {
        return this.selectedBranchIds().includes(id);
    }

    selectAllModalBranches() {
        const current = new Set(this.selectedBranchIds());
        this.filteredBranches().filter((b: any) => b.calculateMrp).forEach((b: any) => current.add(b.id));
        this.selectedBranchIds.set(Array.from(current));
    }

    deselectAllModalBranches() {
        const current = new Set(this.selectedBranchIds());
        this.filteredBranches().forEach((b: any) => current.delete(b.id));
        this.selectedBranchIds.set(Array.from(current));
    }

    // --- DETAILS MODAL ---
    selectedExecutionDetails = signal<ExecutionDetails | null>(null);
    isDetailsModalOpen = signal<boolean>(false);
    isLoadingDetails = signal<boolean>(false);

    openDetailsModal(id: string) {
        this.isDetailsModalOpen.set(true);
        this.isLoadingDetails.set(true);
        this.selectedExecutionDetails.set(null);
        this.productsSummary.set(null);
        this.purchaseRequests.set(null);
        this.expandedTab.set('accuracy');

        const details = this.mrpService.getExecutionDetails(id);
        this.selectedExecutionDetails.set(details);
        this.isLoadingDetails.set(false);

        // Auto-load expanded data for completed executions
        if (details && (details.status === 'Completed' || details.status === 'DocumentsGenerated')) {
            this.loadExpandedDetails(details.uuid);
        }
    }

    closeDetailsModal() {
        this.isDetailsModalOpen.set(false);
        this.selectedExecutionDetails.set(null);
        this.productsSummary.set(null);
        this.purchaseRequests.set(null);
    }

    // --- EXPANDED DETAILS ---
    isLoadingExpanded = signal<boolean>(false);
    expandedTab = signal<'accuracy' | 'purchase'>('accuracy');
    productsSummary = signal<ProductsSummary | null>(null);
    purchaseRequests = signal<PurchaseRequest[] | null>(null);

    private loadExpandedDetails(executionId: string) {
        this.isLoadingExpanded.set(true);
        forkJoin({
            summary: this.mrpService.getProductsSummary(executionId),
            purchases: this.mrpService.getPurchaseRequests(executionId)
        }).subscribe({
            next: ({ summary, purchases }) => {
                this.productsSummary.set(summary);
                this.purchaseRequests.set(purchases);
                this.isLoadingExpanded.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar detalhes expandidos:', err);
                this.isLoadingExpanded.set(false);
            }
        });
    }

    getAccuracyPercentage(): number {
        const s = this.productsSummary();
        if (!s) return 0;
        const divergences = s.physicalExcessCount + s.physicalDeficitCount;
        if (divergences === 0) return 100;
        const total = s.matchedCount + divergences;
        return Math.round((s.matchedCount / total) * 10000) / 100;
    }

    executeNewBatch() {
        const profileIds = this.selectedProfileIds();
        const branchIds = this.selectedBranchIds();

        this.mrpService.createExecutionBatch(profileIds, branchIds);
        this.closeNewExecutionModal();
    }
}
